import React, { useEffect, useState } from 'react';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import styles from './InterviewPriority.module.css';
import { Collapse } from '@mui/material';
import Footer from '../.shared/footer/footer';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Header from '../.shared/header/header';
import CoinStackIcon from '../CustomIcons/CoinStackIcon';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';

export default function InterviewPriority({ goToPreviousStep }) {
    const { updateMultipleFields, interviewData } = useInterview();
    const [ selectedPriority, setSelectedPriority ] = useState('none');

    const navigate = useNavigate();

    const prioritiesLabels = {
        affordability: 'Affordability',
        speed: 'Speed',
        results: 'Best Results'
    };

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.priority) {
            setSelectedPriority(interviewData.priority);
        }
        
        // Track when user reaches the priority step
                trackPostHogEvent('interview_step_viewed', {
                    interview_step: 'priority',
                    step_number: 4,
                    has_existing_data: !!interviewData.priority
                });
        }, [interviewData.priority]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedPriority === 'none' && !recentlyChosen) return;

        const chosenPriority = recentlyChosen ? recentlyChosen : selectedPriority;

        setSelectedPriority(chosenPriority);

        // Set default frequency (can be changed later if needed)
        const defaultFrequency = 'monthly';

        // Determine medication based on priority
        let medication;
        if (chosenPriority === 'affordability') {
            medication = 'semaglutide';
        } else if (chosenPriority === 'speed' || chosenPriority === 'results') {
            medication = 'tirzepatide';
        } else {
            medication = 'semaglutide'; // Default fallback
        }
        
        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateMultipleFields({
                priority: chosenPriority,
                frequency: defaultFrequency,
                medication: medication
            });
            
            // Track priority selection
            trackPostHogEvent('interview_priority_selected', {
                selected_priority: chosenPriority,
                priority_label: prioritiesLabels[chosenPriority],
                interview_step: 'priority',
                step_number: 3
            });
            
            // Track the automatic frequency setting and medication determination
            trackPostHogEvent('interview_frequency_auto_selected', {
                auto_frequency: defaultFrequency,
                determined_medication: medication,
                priority_basis: chosenPriority,
                skipped_frequency_step: true
            });

            // Navigate to result
            navigate(`/result?medication=${medication}&frequency=${defaultFrequency}`);
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(-1);
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header />
                <ProgressBar progress={100} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.priorityContent}>
                        <h1 className={styles.title}>Which is most important for your success?</h1>
                        <div className={styles.buttonsContainer}>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'affordability' ? styles.selected : ''}`} onClick={() => handleConfirm('affordability')}>
                                <CoinStackIcon sx={{ color: `${selectedPriority === 'affordability' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Affordability</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'speed' ? styles.selected : ''}`} onClick={() => handleConfirm('speed')}>
                                <SpeedIcon sx={{ color: `${selectedPriority === 'speed' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Speed</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'results' ? styles.selected : ''}`} onClick={() => handleConfirm('results')}>
                                <TrendingUpIcon sx={{ color: `${selectedPriority === 'results' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Best Results</span>
                            </button>

                            <div className={styles.confirmationButtons}>
                                <button 
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button 
                                    className={`${styles.primaryButton} ${selectedPriority === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    See My Results
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
